// libs
import { Text } from 'ink';
import { useEffect, useState } from 'react';

// components
import BreakpointNotice from '../../../components/BreakpointNotice';
import BreakpointPrompt from '../../../components/BreakpointPrompt';
import BuildProgress from '../../../components/BuildProgress';
import { CanceledError } from '../../../components/CancelBuild';
import ErrorDisplay from '../../../components/ErrorDisplay';
import MainLayout from '../../../components/MainLayout';
import Preparation from './Preparation';

// utils
import logForCI from '../../../utilities/logForCI';
import runBuild from '../utilities/runBuild';
import type { BuildFlags, BuildState } from '../utilities/types';
import useExit from '../../../utilities/useExit';

export default function Build({
  commandUsed,
  configPath,
  flags,
}: {
  commandUsed: string;
  configPath?: string;
  flags: BuildFlags;
}) {
  const exit = useExit();
  const [state, setState] = useState<BuildState>({ state: 'initializing' });
  const [abortController] = useState(() => new AbortController());

  useEffect(() => {
    runBuild({
      configPath,
      flags,
      signal: abortController.signal,
      updateState,
    }).catch((e) => {
      if (!CanceledError.isCanceled(e)) {
        const error = e.response ? e.response.data : e;
        logForCI(error);
        updateState({ state: 'error', error });
      }
    });

    return () => state.onUnmount?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function updateState(changes: Partial<BuildState>) {
    setState(
      (previousState) => ({ ...previousState, ...changes }) as BuildState,
    );

    if (changes.state === 'exit-after-uploading') {
      setTimeout(() => exit(), 10);
    }

    if (changes.state === 'build-failed') {
      setTimeout(() => exit(new Error('Build has failed')), 10);
    }
  }

  switch (state.state) {
    case 'build-failed':
    case 'building': {
      if (!state.build) {
        return (
          <ErrorDisplay commandUsed={commandUsed} error="No build is set" />
        );
      }

      return (
        <MainLayout
          abortController={abortController}
          appId={state.appId}
          appName={state.build.appName || state.appPkg?.name || ''}
          appVersion={state.build.appVersion || state.appPkg?.version || ''}
          build={state.build}
          commandUsed={commandUsed}
          hasBuildEverFailed={state.state === 'build-failed'}
        >
          <BreakpointNotice plan={state.breakpointPlan} />
          <BreakpointPrompt appId={state.appId} build={state.build} />
          <BuildProgress build={state.build} onBuildFailure={() => {}} />
        </MainLayout>
      );
    }

    case 'error':
      return <ErrorDisplay commandUsed={commandUsed} error={state.error} />;

    case 'exit-after-uploading':
      return (
        <Text>
          App is building on ToDesktop. Exit since the --async flag is passed.
        </Text>
      );

    case 'initializing':
      return <Text>Getting app info...</Text>;

    case 'preparing':
      return (
        <MainLayout
          abortController={abortController}
          appId={state.appId}
          appName={state.build?.appName || state.appPkg?.name || ''}
          appVersion={state.build?.appVersion || state.appPkg?.version || ''}
          build={state.build}
          commandUsed={commandUsed}
          hasBuildEverFailed={false}
        >
          <BreakpointNotice plan={state.breakpointPlan} />
          <Preparation
            progressPercentage={state.preparationProgress || 0}
            stageLabel={state.preparationStageLabel || 'Preparing'}
            uploadedSize={state.preparationUploadedSize}
          />
        </MainLayout>
      );

    default:
      return (
        <ErrorDisplay
          commandUsed={commandUsed}
          error={new Error(`Unknown state ${(state as BuildState).state}`)}
        />
      );
  }
}
